import { features } from './features/features';

/**
 * Filters available bundle upgrades based on the current features a user has.
 *
 * Business Rules:
 * - Users with MINI can upgrade to BASIC or EXTRA
 * - Users with HSE (which includes SUBSTANCES) cannot upgrade to MINI because MINI lacks SUBSTANCES
 * - Users with ASSETS or SUBSTANCES modules cannot upgrade to MINI
 * - All users can upgrade to BASIC or EXTRA
 *
 * @param currentFeatures - Array of feature IDs the user currently has
 * @param allBundles - Array of bundle IDs to filter (e.g., ['MINI', 'BASIC', 'EXTRA'])
 * @returns Filtered array of bundle IDs that are valid upgrades for the user
 *
 * @example
 * // User with HMS package (includes SUBSTANCES)
 * getAvailableBundleUpgrades(['ISSUES', 'RISK', 'SUBSTANCES'], ['MINI', 'BASIC', 'EXTRA'])
 * // Returns: ['BASIC', 'EXTRA']
 *
 * @example
 * // User with only MEETINGS module
 * getAvailableBundleUpgrades(['MEETINGS'], ['MINI', 'BASIC', 'EXTRA'])
 * // Returns: ['MINI', 'BASIC', 'EXTRA']
 */
export function getAvailableBundleUpgrades(currentFeatures: string[], allBundles: string[]): string[] {
  // If no bundles to filter, return empty array
  if (!allBundles || allBundles.length === 0) {
    return [];
  }

  // Check if user has features that prevent MINI upgrade
  const hasSubstances = currentFeatures.includes('SUBSTANCES');
  const hasAssets = currentFeatures.includes('ASSETS');

  // MINI bundle lacks SUBSTANCES and ASSETS, so users who already have these cannot downgrade to MINI
  const cannotUpgradeToMini = hasSubstances || hasAssets;

  // Filter bundles
  return allBundles.filter((bundleId) => {
    // If this is MINI and user cannot upgrade to it, exclude it
    if (bundleId === 'MINI' && cannotUpgradeToMini) {
      return false;
    }

    // All other bundles are available
    return true;
  });
}

/**
 * Helper function to check if a bundle contains specific features.
 * This is useful for understanding what features come with each bundle.
 *
 * @param bundleId - The bundle ID to check (e.g., 'MINI', 'BASIC')
 * @returns Array of feature IDs included in the bundle, or undefined if bundle not found
 */
export function getBundleFeatures(bundleId: string): string[] | undefined {
  const bundle = features.find((f) => f.id === bundleId && f.bundle);
  return bundle?.features;
}

/**
 * Helper function to check if upgrading to a target bundle would be a downgrade
 * (losing features the user currently has).
 *
 * @param currentFeatures - Array of feature IDs the user currently has
 * @param targetBundleId - The bundle ID to check (e.g., 'MINI', 'BASIC')
 * @returns true if the upgrade would result in losing features, false otherwise
 */
export function wouldLoseFeatures(currentFeatures: string[], targetBundleId: string): boolean {
  const targetBundleFeatures = getBundleFeatures(targetBundleId);

  // If we can't find the bundle, assume no feature loss
  if (!targetBundleFeatures) {
    return false;
  }

  // Check if any current feature would be lost
  return currentFeatures.some((feature) => !targetBundleFeatures.includes(feature));
}
